import { pushEvent } from "@/lib/gtm";
import { Head } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { salonpageMagazineRedirectUrl } from "@/lib/_url";
import { authApiOrigin } from "@/lib/_url/_origin";
import { fetchAuthApiSession } from "@/lib/_url/_session";
import { NoroshiPhoneAuthModal } from "../salonpage/_noroshi-phone-auth-modal";
import type { MagazineRedirectProps as Props } from "./_props";

type SessionState = "checking" | "signed-in" | "signed-out";

// =====================================================================================
// Constants
// =====================================================================================

function LoginWallPanel({
  salonName,
  onStartAuth,
}: {
  salonName: string;
  onStartAuth: () => void;
}) {
  return (
    <>
      <div
        className="fixed inset-0 z-[9999] bg-transparent"
        aria-hidden="true"
      />
      <div className="fixed inset-x-0 bottom-0 z-[10000] flex items-end justify-center">
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-wall-title"
          className="w-full max-w-md animate-[login-wall-slide-up_260ms_cubic-bezier(0.22,1,0.36,1)] rounded-t-2xl bg-white px-6 pb-8 pt-6 shadow-2xl"
        >
          <div className="mx-auto mb-6 h-1 w-10 rounded-full bg-gray-300" />

          <div className="mb-6 flex flex-col items-center gap-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 text-gray-700">
              <svg
                aria-hidden="true"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 10.5V7.75a4.5 4.5 0 0 0-9 0v2.75m-.75 0h10.5A1.75 1.75 0 0 1 19 12.25v6A1.75 1.75 0 0 1 17.25 20H6.75A1.75 1.75 0 0 1 5 18.25v-6a1.75 1.75 0 0 1 1.75-1.75Z"
                />
              </svg>
            </div>
            <div className="space-y-2">
              <h2
                id="login-wall-title"
                className="text-[22px] font-bold leading-snug text-gray-900"
              >
                予約を続ける前に
              </h2>
              <p className="text-sm leading-relaxed text-gray-500">
                本人確認のため、電話番号認証が必要です
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onStartAuth}
            aria-label={`${salonName}の予約を続けるため電話番号認証する`}
            className="flex w-full items-center justify-center rounded-full bg-red-600 px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
          >
            電話番号認証する
          </button>
        </section>
      </div>
    </>
  );
}

// =====================================================================================
// Page
// =====================================================================================

export default function MagazineRedirect({ salon, featureToggles }: Props) {
  const [sessionState, setSessionState] =
    useState<SessionState>("checking");
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    let active = true;

    // same-origin GW `/api/session`（magazine ホストが salonpage-gateway* で terminate される前提）
    fetchAuthApiSession(authApiOrigin())
      .then((session) => {
        if (active) {
          setSessionState(session.signedIn ? "signed-in" : "signed-out");
        }
      })
      .catch(() => {
        if (active) setSessionState("signed-out");
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (sessionState !== "signed-in") {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [sessionState]);

  useEffect(() => {
    if (!salon || sessionState !== "signed-in") return;

    const timer = setTimeout(() => {
      window.location.replace(salonpageMagazineRedirectUrl(salon.hpbSalonId));
    }, 900);

    return () => {
      clearTimeout(timer);
    };
  }, [salon, sessionState]);

  if (!salon) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#fafaf7]">
        <p className="text-sm text-black/40">Salon not found.</p>
      </main>
    );
  }

  const destinationUrl = salonpageMagazineRedirectUrl(salon.hpbSalonId);
  const showLoginWall = sessionState === "signed-out";
  const isCheckingSession = sessionState === "checking";

  return (
    <>
      <Head title={`${salon.name} に移動中です | Magazine | Hairbook`}>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="min-h-screen bg-[#f4f2ef] text-[#0a0a0a] pb-20 antialiased">
        <main className="px-5 pt-10 sm:pt-16">
          <section className="mx-auto w-full max-w-md">
            <header className="mb-6">
              <p className="text-[11px] font-semibold text-red-600">Hairbook</p>
              <h1 className="mt-1 text-xl font-bold leading-none text-gray-950">
                予約ページに移動します
              </h1>
            </header>

            <article className="overflow-hidden rounded-lg bg-white shadow-[0_18px_50px_rgba(22,24,28,0.12)]">
              <div className="space-y-2 p-5">
                <p className="text-xs font-semibold text-red-600">サロン</p>
                <h2 className="text-[22px] font-bold leading-snug text-gray-950">
                  {salon.name}
                </h2>
                <p className="text-sm font-medium text-gray-500">
                  サロンの予約ページへ移動します。
                </p>
              </div>
            </article>

            <div className="mt-6 space-y-3">
              <a
                href={destinationUrl}
                aria-disabled={sessionState !== "signed-in"}
                className="block w-full rounded-lg bg-red-600 px-4 py-3 text-center text-sm font-semibold text-white transition hover:bg-red-500 aria-disabled:pointer-events-none aria-disabled:bg-red-600/40"
              >
                {isCheckingSession
                  ? "本人確認の状態を確認中です"
                  : "予約ページへ移動中です"}
              </a>
            </div>
          </section>
        </main>

        <style>{`
          @keyframes login-wall-slide-up {
            from {
              opacity: 0;
              transform: translateY(100%);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>

      {showLoginWall && (
        <LoginWallPanel salonName={salon.name} onStartAuth={() => setAuthModalOpen(true)} />
      )}
      {authModalOpen && (
        <NoroshiPhoneAuthModal
          origin={authApiOrigin()}
          category={featureToggles?.useHairbookLogin ? "sms_login_wall_salonpage" : "sms_login_wall_redirect"}
          sourceId={salon.hpbSalonId}
          completeOnCode
          onAuthenticated={() => {
            pushEvent("magazine_auth_complete", { hpb_salon_id: salon.hpbSalonId });
            setSessionState("signed-in");
            setAuthModalOpen(false);
          }}
          onClose={() => setAuthModalOpen(false)}
        />
      )}
    </>
  );
}
